import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { SnackBarService } from 'src/app/common/components/snackbar/snackbar.service';
import { SnackBarData } from 'src/app/common/components/snackbar/model/snackbar.model';
import { UserGroupService } from '../usergroup.service';
import { DirtyCheck } from 'src/app/common/guards/form-deactivate.guard';
import { ConfirmDialogService } from 'src/app/common/components/confirm-dialog/confirm-dialog.service';
import { ConfirmDialogModel } from 'src/app/common/components/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { DataPermissionDialogComponent } from './data-permission-dialog/data-permission-dialog.component';

interface PermissionItem {
  id: string;
  function_name: string;
  original_name: string; // Add this to store the original name/code for permission level checking
  view: boolean;
  add: boolean;
  edit: boolean;
  delete: boolean;
  publish: boolean;
  export: boolean;
  import: boolean;
  exec: boolean;
  has_data_permission?: number;
  // Add applicable flags
  is_view_applicable: boolean;
  is_add_applicable: boolean;
  is_edit_applicable: boolean;
  is_delete_applicable: boolean;
  is_execute_applicable: boolean;
  is_publish_applicable: boolean;
  is_export_applicable: boolean;
  is_import_applicable: boolean;
  available_in?: string[];
}

interface PermissionGroup {
  groupName: string;
  permissions: PermissionItem[];
}

interface SelectAllState {
  checked: boolean;
  indeterminate: boolean;
}

interface SelectAllStates {
  view: SelectAllState;
  add: SelectAllState;
  edit: SelectAllState;
  delete: SelectAllState;
  exec: SelectAllState;
  publish: SelectAllState;
  export: SelectAllState;
  import: SelectAllState;
}

@Component({
  selector: 'app-usergroup-permissions',
  standalone: false,
  templateUrl: './usergroup-permissions.component.html',
  styleUrl: './usergroup-permissions.component.scss'
})
export class UsergroupPermissionsComponent implements OnInit, DirtyCheck {

  userGroup: any;
  isFormDirty: boolean = false;
  searchText: string = '';
  filteredPermissionGroups: PermissionGroup[] = [];
  app: string = '';
  permissionData: any;
  permissionGroups: PermissionGroup[] = [];
  displayedColumns: string[] = ['function', 'view', 'add', 'edit', 'delete', 'publish', 'export', 'import', 'exec'];

  // Add properties for user group list
  userGroupList: any[] = [];

  // Add properties to track initialization
  isDataLoaded: boolean = false;

  // Add loading state
  isUpdating: boolean = false;

  // Add select all states for each permission type
  selectAllStates: SelectAllStates = {
    view: { checked: false, indeterminate: false },
    add: { checked: false, indeterminate: false },
    edit: { checked: false, indeterminate: false },
    delete: { checked: false, indeterminate: false },
    exec: { checked: false, indeterminate: false },
    publish: { checked: false, indeterminate: false },
    export: { checked: false, indeterminate: false },
    import: { checked: false, indeterminate: false }
  };

  // Add flag to track if we have valid groups
  hasValidGroups: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private userGroupService: UserGroupService,
    private snackBarService: SnackBarService,
    private confirmDialogService: ConfirmDialogService,
    private dialog: MatDialog
  ) { }

  isDirty(): boolean {
    return this.isFormDirty;
  }

  ngOnInit(): void {


    // Load all user groups first
    this.loadAllUserGroups();

    // Get stored data first before route params
    const storedData = sessionStorage.getItem('usergroup-permissions-data');
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);

        this.permissionData = parsedData.permissions;
        this.userGroup = parsedData.userGroup;
        this.app = parsedData.app;
        this.isDataLoaded = true;

        // Don't remove sessionStorage yet - wait for route params
      } catch (error) {
        console.error('Error parsing stored permission data:', error);
      }
    }

    // Get query parameters and merge with stored data
    this.route.queryParams.subscribe(params => {


      // If we have route params, use them to override stored data
      if (params['app']) {
        this.app = params['app'];
      }

      // If we have userGroupId but no stored userGroup, we need to fetch the data
      if (params['userGroupId'] && !this.userGroup?.id) {
        // Reload data from API using the route parameters
        this.loadPermissionsFromRoute(params['app'], params['userGroupId']);
      } else if (this.isDataLoaded) {
        // We have stored data, process it
        this.processPermissionData();
        // Now it's safe to clear sessionStorage
        sessionStorage.removeItem('usergroup-permissions-data');
      }

      this.checkDataComplete();
    });

    // Try to get data from navigation state (for new navigations)
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras.state) {
      this.permissionData = navigation.extras.state['permissions'];
      this.userGroup = navigation.extras.state['userGroup'];
      this.app = navigation.extras.state['app'];
      this.isDataLoaded = true;
      this.processPermissionData();
    }
  }

  /**
   * Load permissions from route parameters (used when page is refreshed)
   */
  private loadPermissionsFromRoute(app: string, userGroupId: string): void {
    if (!app || !userGroupId) {
      console.error('Missing app or userGroupId in route parameters');
      return;
    }

    this.isDataLoaded = false;

    this.userGroupService.getPermission(app, parseInt(userGroupId)).subscribe({
      next: (response) => {
        if (response.status === 'SUCCESS') {
          this.permissionData = response.data.permissions || response.data || [];

          // Extract user group information ONLY if we don't have it or it's a different group
          // This prevents overwriting the nice "Name" from userGroupList with a generic one from API
          const currentGroupId = this.userGroup?.id;
          const targetGroupId = parseInt(userGroupId);

          if (currentGroupId !== targetGroupId || !this.userGroup?.name || this.userGroup.name.startsWith('User Group ')) {
            // First, try to find it in our group list if we have it
            const localGroup = this.userGroupList.find(ug => ug.id === targetGroupId);
            if (localGroup) {
              this.userGroup = localGroup;
            } else if (response.data.user_group && response.data.user_group.name && !response.data.user_group.name.startsWith('User Group ')) {
              this.userGroup = response.data.user_group;
            } else if (response.data.usergroup_name || response.data.usergroup) {
              const nameValue = response.data.usergroup_name || response.data.usergroup.name || response.data.usergroup;
              this.userGroup = {
                id: targetGroupId,
                name: nameValue
              };
            } else {
              this.fetchUserGroupDetails(targetGroupId);
            }
          }

          this.app = app;
          this.isDataLoaded = true;
          this.processPermissionData();
        } else {
          console.error('Failed to load permissions:', response.message);
        }
      },
      error: (error) => {
        console.error('Error loading permissions from route:', error);
      }
    });
  }

  /**
   * Fetch user group details separately if not included in permissions response
   */
  private fetchUserGroupDetails(userGroupId: number): void {
    // You'll need to add this method to UserGroupService if it doesn't exist
    this.userGroupService.getById(userGroupId).subscribe({
      next: (response) => {
        if (response.status === 'SUCCESS' && response.data) {
          this.userGroup = {
            id: userGroupId,
            name: response.data.name || response.data.group_name || `User Group ${userGroupId}`
          };
        } else {
          // Fallback if API call fails
          this.userGroup = {
            id: userGroupId,
            name: `User Group ${userGroupId}`
          };
        }
      },
      error: (error) => {
        console.error('Error fetching user group details:', error);
        // Fallback if API call fails
        this.userGroup = {
          id: userGroupId,
          name: `User Group ${userGroupId}`
        };
      }
    });
  }

  /**
   * Load all user groups for the dropdown
   */
  private loadAllUserGroups(): void {
    const requestData = {
      "search": {
        "filters": {
          "scope": {
            "limit": 1000,
            "offset": 0
          }
        },
        "sort": []
      }
    };

    this.userGroupService.getList(requestData).subscribe({
      next: (response) => {
        if (response.status === 'SUCCESS') {
          this.userGroupList = response.data.user_groups || [];

          // If we have a userGroupId from URL, find and set the selected user group
          this.route.queryParams.subscribe(params => {
            if (params['userGroupId']) {
              const userGroupId = parseInt(params['userGroupId']);
              const selectedUserGroup = this.userGroupList.find(ug => ug.id === userGroupId);
              if (selectedUserGroup) {
                this.userGroup = selectedUserGroup;
              }
            }
          });
        } else {
          console.error('Failed to load user groups:', response.message);
        }
      },
      error: (error) => {
        console.error('Error loading user groups:', error);
        let snackBarData: SnackBarData = {
          message: 'Failed to load user groups. Connection to the server failed.',
          title: 'Failed!!!',
          type: 'error'
        };
        this.snackBarService.openSnackBar(snackBarData);
      }
    });
  }

  /**
   * Handle user group selection change
   */
  async onUserGroupChange(selectedUserGroupId: any): Promise<void> {
    const userGroupId = parseInt(selectedUserGroupId);
    if (isNaN(userGroupId)) return;

    if (this.isDirty()) {
      const message = "You have unsaved permission changes. Changing the user group will discard these changes. Do you want to proceed?";
      const dialogData = new ConfirmDialogModel("Discard Unsaved Changes?", message);
      const result = await this.confirmDialogService.openDialog(dialogData);
      if (!result) {
        // Need to prevent the selection from changing or re-set it
        // This is tricky for a standard <select>, so we find the previous group
        return;
      }
    }

    const selectedUserGroup = this.userGroupList.find(ug => ug.id === userGroupId);
    if (selectedUserGroup) {
      this.userGroup = selectedUserGroup;
      this.isFormDirty = false; // Reset dirty flag as we are loading new data

      // Reload permissions for the new user group
      if (this.app && userGroupId) {
        this.loadPermissionsFromRoute(this.app, userGroupId.toString());
      }
    }
  }

  checkDataComplete(): void {
    console.log('Checking data completeness:', {
      app: this.app,
      userGroup: this.userGroup,
      appDisplayName: this.getAppDisplayName(),
      userGroupName: this.getUserGroupName()
    });
  }

  processPermissionData(): void {
    if (!this.permissionData || !Array.isArray(this.permissionData)) {
      return;
    }

    // Group permissions by system_group and system_sub_group
    const groups: { [key: string]: PermissionItem[] } = {};
    let validGroupsFound = false;

    this.permissionData.forEach((permission: any) => {
      if (permission.is_delete == 1) {
        return;
      }
      // Create hierarchical group name using system_group and system_sub_group
      let groupName = '';

      // Check if app is 'oo' or 'crm' - use only system_group
      if (this.app.toLowerCase() === 'oo' || this.app.toLowerCase() === 'crm') {
        groupName = this.getValidGroupName(permission.system_group);
      } else {
        // For other apps, categorize by system_sub_group instead of system_group
        const systemGroup = this.getValidGroupName(permission.system_group);
        const systemSubGroup = this.getValidGroupName(permission.system_sub_group);

        if (systemSubGroup) {
          groupName = systemSubGroup;
        } else if (systemGroup) {
          groupName = systemGroup;
        }
      }

      // If no valid group name found, use a default group
      if (!groupName) {
        groupName = 'General Functions';
      } else {
        validGroupsFound = true;
      }

      if (!groups[groupName]) {
        groups[groupName] = [];
      }

      groups[groupName].push({
        id: permission.id.toString(),
        function_name: permission.display_name || permission.name || permission.code,
        original_name: permission.name || permission.code,
        view: permission.can_view === 1,
        add: permission.can_add === 1,
        edit: permission.can_edit === 1,
        delete: permission.can_delete === 1,
        publish: permission.can_publish === 1,
        export: permission.can_export === 1,
        import: permission.can_import === 1, // <-- Add this line
        exec: permission.can_execute === 1,
        has_data_permission: (permission.has_data_permission == 1 || permission.has_data_permission === '1') ? 1 : 0,
        is_view_applicable: permission.is_view_applicable === 1,
        is_add_applicable: permission.is_add_applicable === 1,
        is_edit_applicable: permission.is_edit_applicable === 1,
        is_delete_applicable: permission.is_delete_applicable === 1,
        is_execute_applicable: permission.is_execute_applicable === 1,
        is_publish_applicable: permission.is_publish_applicable === 1,
        is_export_applicable: permission.is_export_applicable === 1,
        is_import_applicable: permission.is_import_applicable === 1,
        available_in: permission.available_in || []
      });
    });

    // Set the flag based on whether we found valid groups
    this.hasValidGroups = validGroupsFound;

    // Convert to array format and sort by group name
    this.permissionGroups = Object.keys(groups)
      .sort()
      .map(groupName => ({
        groupName,
        permissions: groups[groupName].sort((a, b) => a.function_name.localeCompare(b.function_name))
      }));

    this.applySearchFilter();
    // Update select all states after processing data
    this.updateSelectAllStates();
  }

  applySearchFilter(): void {
    if (!this.searchText || this.searchText.trim() === '') {
      this.filteredPermissionGroups = this.permissionGroups.map(group => ({
        ...group,
        permissions: [...group.permissions]
      }));
      return;
    }
    const search = this.searchText.trim().toLowerCase();
    this.filteredPermissionGroups = this.permissionGroups
      .map(group => ({
        ...group,
        permissions: group.permissions.filter(p => p.function_name.toLowerCase().includes(search))
      }))
      .filter(group => group.permissions.length > 0);
  }

  /**
   * Get valid group name, filtering out empty, null, or meaningless values
   */
  private getValidGroupName(groupValue: any): string {
    if (!groupValue ||
      groupValue === null ||
      groupValue === undefined ||
      groupValue === '' ||
      groupValue.toString().trim() === '' ||
      groupValue.toString().toLowerCase() === 'null' ||
      groupValue.toString().toLowerCase() === 'undefined') {
      return '';
    }

    const trimmedValue = groupValue.toString().trim();
    // You can add more conditions here to filter out other meaningless values
    if (trimmedValue === '0' || trimmedValue === '-') {
      return '';
    }

    return trimmedValue;
  }

  onPermissionChange(permission: PermissionItem, field: string, value: boolean): void {
    // Update the permission object directly
    switch (field) {
      case 'view':
        permission.view = value;
        break;
      case 'add':
        permission.add = value;
        break;
      case 'edit':
        permission.edit = value;
        break;
      case 'delete':
        permission.delete = value;
        break;
      case 'exec':
        permission.exec = value;
        break;
      case 'publish':
        permission.publish = value;
        break;
      case 'export':
        permission.export = value;
        break;
      case 'import':
        permission.import = value;
        break;
    }


    // Update select all states when individual permissions change
    this.updateSelectAllStates();
    this.isFormDirty = true;
  }

  // Add method to update select all states
  private updateSelectAllStates(): void {
    const permissionTypes: (keyof SelectAllStates)[] = ['view', 'add', 'edit', 'delete', 'exec', 'publish', 'export', 'import'];

    permissionTypes.forEach(type => {
      const applicablePermissions = this.getAllApplicablePermissions(type);
      const checkedCount = applicablePermissions.filter(p => this.getPermissionValue(p, type)).length;

      if (checkedCount === 0) {
        this.selectAllStates[type] = { checked: false, indeterminate: false };
      } else if (checkedCount === applicablePermissions.length) {
        this.selectAllStates[type] = { checked: true, indeterminate: false };
      } else {
        this.selectAllStates[type] = { checked: false, indeterminate: true };
      }
    });
  }

  // Add method to get all applicable permissions for a type
  private getAllApplicablePermissions(permissionType: keyof SelectAllStates): PermissionItem[] {
    const allPermissions: PermissionItem[] = [];
    this.permissionGroups.forEach(group => {
      group.permissions.forEach(permission => {
        if (this.isPermissionApplicable(permission, permissionType)) {
          allPermissions.push(permission);
        }
      });
    });
    return allPermissions;
  }

  // Add method to get permission value
  private getPermissionValue(permission: PermissionItem, permissionType: keyof SelectAllStates): boolean {
    switch (permissionType) {
      case 'view':
        return permission.view;
      case 'add':
        return permission.add;
      case 'edit':
        return permission.edit;
      case 'delete':
        return permission.delete;
      case 'exec':
        return permission.exec;
      case 'publish':
        return permission.publish;
      case 'export':
        return permission.export;
      case 'import':
        return permission.import;
      default:
        return false;
    }
  }

  // Add method to handle select all for a specific permission type
  onSelectAll(permissionType: keyof SelectAllStates, checked: boolean): void {
    this.permissionGroups.forEach(group => {
      group.permissions.forEach(permission => {
        // Only update if the permission type is applicable
        const isApplicable = this.isPermissionApplicable(permission, permissionType);
        if (isApplicable) {
          switch (permissionType) {
            case 'view':
              permission.view = checked;
              break;
            case 'add':
              permission.add = checked;
              break;
            case 'edit':
              permission.edit = checked;
              break;
            case 'delete':
              permission.delete = checked;
              break;
            case 'exec':
              permission.exec = checked;
              break;
            case 'publish':
              permission.publish = checked;
              break;
            case 'export':
              permission.export = checked;
              break;
            case 'import':
              permission.import = checked;
              break;
          }
        }
      });
    });

    // Update the select all states
    this.updateSelectAllStates();
    this.isFormDirty = true;
  }

  // Add method to check if a permission type is applicable
  private isPermissionApplicable(permission: PermissionItem, permissionType: keyof SelectAllStates): boolean {
    switch (permissionType) {
      case 'view':
        return permission.is_view_applicable;
      case 'add':
        return permission.is_add_applicable;
      case 'edit':
        return permission.is_edit_applicable;
      case 'delete':
        return permission.is_delete_applicable;
      case 'exec':
        return permission.is_execute_applicable;
      case 'publish':
        return permission.is_publish_applicable;
      case 'export':
        return permission.is_export_applicable;
      case 'import':
        return permission.is_import_applicable;
      default:
        return false;
    }
  }

  onUpdate(): void {


    if (!this.app || !this.userGroup?.id || this.permissionGroups.length === 0) {
      console.error('Missing required data for update:', {
        app: this.app,
        userGroupId: this.userGroup?.id,
        permissionGroups: this.permissionGroups.length
      });
      let snackBarData: SnackBarData = {
        message: 'Missing required data for update. Please reload the page.',
        title: 'Update Failed',
        type: 'error'
      };
      this.snackBarService.openSnackBar(snackBarData);
      return;
    }

    // Prepare permissions data for API in the format expected by backend
    const permissionsToUpdate: any[] = [];

    this.permissionGroups.forEach(group => {
      group.permissions.forEach(permission => {
        const permissionData = {
          system_function_id: parseInt(permission.id),
          can_view: permission.view ? 1 : 0,
          can_add: permission.add ? 1 : 0,
          can_edit: permission.edit ? 1 : 0,
          can_delete: permission.delete ? 1 : 0,
          can_execute: permission.exec ? 1 : 0,
          can_publish: permission.publish ? 1 : 0,
          can_export: permission.export ? 1 : 0,
          can_import: permission.import ? 1 : 0,
          publish_status: 0
        };


        permissionsToUpdate.push(permissionData);
      });
    });


    this.isUpdating = true;
    console.log(permissionsToUpdate);
    this.userGroupService.updatePermissions(this.app, this.userGroup.id, permissionsToUpdate)
      .subscribe({
        next: (response) => {
          this.isUpdating = false;
          this.isFormDirty = false;
          let snackBarData: SnackBarData = {
            type: 'success',
            title: 'Success',
            message: `Permissions updated successfully for ${this.getUserGroupName()} in ${this.getAppDisplayName()}!`
          };
          this.snackBarService.openSnackBar(snackBarData);
        },
        error: (error) => {
          console.error('Error updating permissions:', error);
          console.error('Error details:', {
            status: error.status,
            statusText: error.statusText,
            message: error.message,
            error: error.error
          });
          this.isUpdating = false;

          let errorMessage = 'Error updating permissions. ';
          if (error.status === 500) {
            errorMessage += 'Server error occurred. Please check the server logs and ensure the route is configured correctly.';
          } else if (error.status === 404) {
            errorMessage += 'API endpoint not found. Please check the route configuration.';
          } else if (error.status === 400) {
            errorMessage += 'Bad request. Please check the data format.';
          } else {
            errorMessage += `Status: ${error.status}. Please try again.`;
          }

          let snackBarData: SnackBarData = {
            message: errorMessage,
            title: 'Update Failed!!!',
            type: 'error'
          };
          this.snackBarService.openSnackBar(snackBarData);
        }
      });
  }

  onDiscard(): void {
    this.location.back();
  }

  onReload(): void {

    // TODO: Implement reload functionality
    window.location.reload();
  }

  getAppDisplayName(): string {
    const appNames: { [key: string]: string } = {
      'eps': 'EPS',
      'pos': 'POS',
      'oo': 'OO',
      'crm': 'CRM',
      'wfm': 'WFM',
      'csms': 'CSMS',
      'oss': 'OSS'
    };

    const normalizedApp = this.app.toLowerCase().trim() || '';
    const result = appNames[normalizedApp] || (this.app ? this.app.toUpperCase() : 'Unknown Application');
    return result;
  }

  getUserGroupName(): string {
    const result = this.userGroup?.name || 'Unknown User Group';
    return result;
  }

  /**
   * Get the display label for permission level based on available_in
   */
  getPermissionLevelLabel(permission: PermissionItem): string {
    if (permission.available_in && Array.isArray(permission.available_in) && permission.available_in.length === 1) {
      return `( ${permission.available_in[0]} Only )`;
    }
    return '';
  }

  /**
   * Get the CSS class for permission level styling
   */
  getPermissionLevelClass(permission: PermissionItem): string {
    if (permission.available_in && Array.isArray(permission.available_in) && permission.available_in.length === 1) {
      return 'only-label';
    }
    return '';
  }

  /**
   * Get global index for serial numbering when groups are hidden
   */
  getGlobalIndex(groupIndex: number, itemIndex: number): number {
    let globalIndex = 0;
    for (let i = 0; i < groupIndex; i++) {
      globalIndex += this.permissionGroups[i].permissions.length;
    }
    return globalIndex + itemIndex;
  }

  navBack() {
    this.router.navigate(['/user/user-group']);
  }

  /**
   * Open Data Permission popup modal for a specific function
   */
  openDataPermissionModal(permission: PermissionItem): void {
    const dialogRef = this.dialog.open(DataPermissionDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      panelClass: 'custom-data-permission-dialog-panel',
      data: {
        userGroupId: this.userGroup?.id,
        functionId: parseInt(permission.id),
        functionName: permission.function_name
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('Data permissions updated for function:', permission.function_name);
      }
    });
  }
}
